import { useEffect, useState } from "react";
import { GlassWater, Sun, Home, Square, Play, BellOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useLang } from "@/context/LangContext";
import { useAlerts } from "@/context/AlertsContext";
import { fill } from "@/lib/utils";
import { LANG_TAG } from "@/lib/geo";
import { fmtCountdown } from "@/lib/prayerTimes";
import { readHydration, writeHydration, isActive, nextDrinkAt, startHydration, stopHydration, dayKey, drinksToday, AUTO_TIERS, EVERY_MIN } from "@/lib/hydration";

// "Time to drink" reminders every 30 min: manual start, or automatic when it is very hot and the pilgrim
// has marked themselves outdoors. In-app countdown; pop-up dialog app-wide (AlertsContext); push when permitted.
export function HydrationReminder({ tier, now, maghribAt }) {
  const { tc, lang } = useLang();
  const alerts = useAlerts();
  const [s, setS] = useState(() => { const h = readHydration(); return h.outdoorDay && h.outdoorDay !== dayKey() ? writeHydration({ ...h, outdoor: false }) : h; });
  const [busy, setBusy] = useState(false);
  const nowMs = now.getTime();
  const active = isActive(s, nowMs);
  const nextAt = nextDrinkAt(s, nowMs);
  const hot = !!tier;
  const texts = {
    title: tc({ id: "Waktunya minum air", en: "Time to drink water", ar: "وقت شرب الماء" }),
    body: tc({ id: "Cuaca sangat panas — minum 200–250 ml air sekarang, sedikit demi sedikit.", en: "It's very hot — drink 200–250 ml of water now, in small sips.", ar: "الجو حار جدًا — اشرب 200–250 مل من الماء الآن على رشفات." }),
  };
  const fmtTime = (ms) => new Date(ms).toLocaleTimeString(LANG_TAG[lang] || "en-US", { hour: "2-digit", minute: "2-digit" });

  const start = async (by = "manual") => {
    setBusy(true);
    const ns = await startHydration({ maghribAt, by, texts });
    setS(ns); setBusy(false);
    toast.success(fill(tc({ id: "Pengingat minum aktif tiap {min} menit sampai {time}", en: "Drink reminders on every {min} min until {time}", ar: "تذكير الشرب مفعّل كل {min} دقيقة حتى {time}" }), { min: EVERY_MIN, time: fmtTime(ns.until) }));
    if (ns.pushOk === false) toast.message(tc({ id: "Notifikasi tidak tersedia — pengingat hanya saat aplikasi terbuka", en: "Notifications unavailable — reminders only while the app is open", ar: "الإشعارات غير متاحة — التذكير فقط أثناء فتح التطبيق" }));
  };
  const stop = async (manual = true) => { setBusy(true); setS(await stopHydration({ manual })); setBusy(false); };
  const setOutdoor = (outdoor) => setS(writeHydration({ ...readHydration(), outdoor, outdoorDay: dayKey() }));
  const setAuto = (auto) => setS(writeHydration({ ...readHydration(), auto }));

  // Auto-start when very hot + outdoors (unless the user stopped it today); auto-stop when back indoors.
  useEffect(() => {
    if (s.auto && s.outdoor && AUTO_TIERS.has(tier?.id) && !active && s.stoppedDay !== dayKey() && !busy) start("auto");
    else if (active && s.by === "auto" && !s.outdoor) stop(false);
  }, [s.auto, s.outdoor, tier?.id, active]); // eslint-disable-line react-hooks/exhaustive-deps

  // Session expired → clear; otherwise mirror changes made by the app-wide DrinkAlertDialog (drank / stopped).
  useEffect(() => {
    const cur = readHydration();
    if (cur.until && cur.until <= nowMs) setS(writeHydration({ ...cur, until: null, startedAt: null, by: null, firedSlot: 0, snoozeAt: null }));
    else if (cur.until !== s.until || cur.firedSlot !== s.firedSlot || cur.drinks !== s.drinks) setS(cur);
  }, [nowMs]); // eslint-disable-line react-hooks/exhaustive-deps
  const glasses = drinksToday(s);

  return (
    <div className="rounded-2xl bg-sky-500/10 ring-1 ring-sky-500/30 px-4 py-3 space-y-2" data-testid="hydration-card" data-active={active || undefined} data-by={s.by || undefined}>
      <div className="flex items-center gap-2">
        <GlassWater className="h-4 w-4 text-sky-600 dark:text-sky-300" />
        <p className="flex-1 text-sm font-semibold text-sky-800 dark:text-sky-200">{tc({ id: "Pengingat minum air", en: "Drink-water reminder", ar: "تذكير شرب الماء" })}</p>
        <button onClick={() => alerts?.showDrinkAlert()} data-testid="hydration-popup-test" className="text-[11px] text-sky-700 dark:text-sky-300 underline-offset-2 hover:underline">
          {tc({ id: "Uji pop-up", en: "Test pop-up", ar: "تجربة النافذة" })}
        </button>
        <label className="inline-flex items-center gap-2 text-[11px] text-muted-foreground">
          {tc({ id: "Otomatis saat sangat panas", en: "Auto when very hot", ar: "تلقائي عند الحر الشديد" })}
          <Switch checked={s.auto} onCheckedChange={setAuto} data-testid="hydration-auto" aria-label={tc({ id: "Otomatis saat sangat panas", en: "Auto when very hot", ar: "تلقائي عند الحر الشديد" })} />
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-full bg-background/70 p-0.5" role="group" data-testid="hydration-outdoor-group">
          {[{ v: true, icon: Sun, label: { id: "Di luar ruangan", en: "Outdoors", ar: "في الخارج" } }, { v: false, icon: Home, label: { id: "Di dalam", en: "Indoors", ar: "في الداخل" } }].map((o) => (
            <button key={String(o.v)} onClick={() => setOutdoor(o.v)} aria-pressed={s.outdoor === o.v} data-testid={o.v ? "hydration-outdoor" : "hydration-indoor"}
              className={`inline-flex h-9 items-center gap-1 rounded-full px-3 text-xs font-medium transition-colors ${s.outdoor === o.v ? "bg-sky-600 text-white" : "text-muted-foreground hover:text-foreground"}`}>
              <o.icon className="h-3 w-3" />{tc(o.label)}
            </button>
          ))}
        </div>
        {active ? (
          <button onClick={() => stop(true)} disabled={busy} data-testid="hydration-stop" className="inline-flex h-9 items-center gap-1 rounded-full border border-sky-600/40 px-3 text-xs font-medium text-sky-700 dark:text-sky-300 hover:bg-sky-500/10">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Square className="h-3 w-3" />}{tc({ id: "Hentikan", en: "Stop", ar: "إيقاف" })}
          </button>
        ) : (
          <button onClick={() => start("manual")} disabled={busy} data-testid="hydration-start" className="inline-flex h-9 items-center gap-1 rounded-full bg-sky-600 px-3 text-xs font-medium text-white hover:opacity-90 disabled:opacity-60">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}{fill(tc({ id: "Mulai (tiap {min} mnt)", en: "Start (every {min} min)", ar: "ابدأ (كل {min} د)" }), { min: EVERY_MIN })}
          </button>
        )}
      </div>

      <p className="text-[11px] text-muted-foreground tabular-nums" data-testid="hydration-status">
        {active ? (
          <>
            {tc({ id: "Aktif", en: "Active", ar: "مفعّل" })}{s.by === "auto" ? ` (${tc({ id: "otomatis", en: "auto", ar: "تلقائي" })})` : ""} · {tc({ id: "sampai", en: "until", ar: "حتى" })} {fmtTime(s.until)}
            {nextAt && <> · {tc({ id: "minum berikutnya", en: "next drink", ar: "الشربة التالية" })} <b className="text-foreground" data-testid="hydration-next">{fmtCountdown(nextAt - nowMs)}</b></>}
            {glasses > 0 && <> · <span data-testid="hydration-glasses">{fill(tc({ id: "{n} gelas hari ini", en: "{n} glasses today", ar: "{n} أكواب اليوم" }), { n: glasses })}</span></>}
            {s.pushOk === false && <span className="inline-flex items-center gap-1 ms-1"><BellOff className="h-3 w-3" />{tc({ id: "tanpa notifikasi", en: "no notifications", ar: "بدون إشعارات" })}</span>}
          </>
        ) : hot ? (
          s.outdoor
            ? tc({ id: "Belum aktif — tekan Mulai, atau nyalakan Otomatis untuk mulai sendiri saat sangat panas.", en: "Not active — press Start, or turn on Auto to start by itself when it is very hot.", ar: "غير مفعّل — اضغط ابدأ، أو فعّل التلقائي ليبدأ وحده عند الحر الشديد." })
            : tc({ id: "Tandai 'Di luar ruangan' saat keluar agar pengingat berjalan otomatis ketika sangat panas.", en: "Mark 'Outdoors' when you head out so reminders start automatically when it is very hot.", ar: "اختر 'في الخارج' عند خروجك ليبدأ التذكير تلقائيًا عند الحر الشديد." })
        ) : (
          tc({ id: "Cuaca nyaman — pengingat otomatis akan aktif saat sangat panas.", en: "Comfortable weather — automatic reminders kick in when it is very hot.", ar: "الجو معتدل — سيبدأ التذكير التلقائي عند الحر الشديد." })
        )}
      </p>
    </div>
  );
}
